/**
 * `useLotStateForRecord` — one lot's state for the bid record (BL-1542 AC 27–31,
 * AC 38; ADR 053 — Console Scale § 4.1).
 *
 * THE PROBLEM IT SOLVED. `LotBidRecord` mounts with a `lotId` and nothing else,
 * at three sites: the clerk console's centre panel, the lot editor's Bids tab,
 * and the standalone slide-out. Only the first has an auction-keyed ancestor.
 * `useLot` cannot serve the other two — `useLotStateStore()` THROWS without a
 * `<RealtimeLotsProvider>`, and that throw IS the ancestor problem this item
 * exists to remove.
 *
 * ONE SOURCE, BOTH PATHS (AC 38). This hook used to branch: the console read the
 * rail's fold, and off-console it subscribed to the PUBLIC `lot:{id}:snapshot`,
 * because the admin lot-state topic was per-AUCTION and its catch-up authorized
 * on an `auctionId` the unit does not have.
 *
 * That branch is GONE. BL-1542's stage 2b narrowed the admin stream instead of
 * reaching for the public one: the catch-up accepts `?lot_id=` and resolves the
 * auction server-side, and `RealtimeLotsProvider` takes an optional singular
 * `lotId`. So `LotBidRecord` supplies its OWN lot-scoped provider off-console,
 * a store is always present, and this hook reads the fold — full stop.
 *
 * Why that is better than the branch it replaces, and not merely tidier:
 *
 *   • **The sale record can exist at all.** `hammer_price` and
 *     `winner_customer_id` are barred from the public snapshot — BL-1519 removed
 *     the hammer with a standing "Do NOT re-add it", BL-645 excluded the winner
 *     because that payload rides the public/anon bidder channel. On the admin
 *     stream they are clerk-only and fan-in on the lot row.
 *   • **The two paths cannot drift.** They were narrowing two DIFFERENT payloads
 *     onto one shape; now there is one payload and one narrowing.
 *   • **The off-console seed is O(1).** `?lot_id=` returns one row rather than
 *     the auction's catalog.
 *
 * WHAT REMAINS OF THE DETECTION. The store is read NULLABLY and an absent one
 * yields `undefined` rather than throwing. `LotBidRecord` guarantees a provider,
 * so that path is not reachable from the unit — it is there so a future caller
 * that forgets gets an empty record rather than a crash, and so this hook stays
 * callable unconditionally (rule #7).
 *
 * @sideEffect none. It reads a store someone else owns; the subscription belongs
 *   to whichever `<RealtimeLotsProvider>` is above.
 */
'use client';

import { useMemo } from 'react';

import type { AdminLotStateDeltaDecoded } from '../../mappers/realtime/admin-lot-state';
import type { Money } from '../../isomorphic/money/money';
import { useOptionalLot } from './useOptionalLot';

/**
 * The fields `LotBidRecord` reads off lot state.
 *
 * Deliberately NOT `AdminLotStateDeltaDecoded` itself — that type carries rail-only
 * fields (thumbnail, lot number, ring) the record never reads, and a narrowed
 * shape is what keeps the record's dependency on the stream legible.
 *
 * Every decision stamp is optional because BL-1554 omits a null stamp from the
 * wire — an unreached stamp arrives as an ABSENT key, not an explicit `null`.
 * Readers guard with `== null`, which cannot tell the two apart.
 */
export interface LotStateForRecord {
    /** Gates whether the Pre-bids section renders. */
    preBiddingAt?: string | null;
    /** Positions the pre-bid divider. */
    biddingOpenedAt?: string | null;
    pausedAt?: string | null;
    closeTime: string | null;
    deletedAt?: string | null;
    withdrawnAt?: string | null;
    hammeredAt?: string | null;
    closeProcessedAt?: string | null;
    bidsPlacedCount: number;
    /** The live accepted bid as `Money` in the lot's currency (BL-2009), or `null`. */
    acceptedBidAmount: Money | null;
    /**
     * The recorded SALE price as `Money` in the lot's currency (BL-1542 AC 36,
     * BL-2009), `null` for a lot's entire live phase. A zero `Money` is a real
     * sale, so consumers guard with `!= null`, never truthiness.
     */
    hammerPrice: Money | null;
    /**
     * The settled buyer's `customers.id`, `null` until the lot is hammered.
     *
     * NOT the standing high bidder — that is a live position that changes with
     * every bid, and the public snapshot's `currentBidderCustomerId` is the one
     * that means it. This is the recorded outcome, and it survives a buyer swap.
     */
    winnerCustomerId: string | null;
    /**
     * BL-2324: the lot's pricing basis (`'per_acre'` / `'total'`, `null` off
     * land) and its acreage as a decimal string. The record reads them through
     * `isPerAcrePricing` to label each bidding-plane figure "/ac" (ADR 035 —
     * Money Units), off the SAME stream as every other lot fact here (rule #32),
     * so the unit follows a basis change live.
     */
    pricingType: string | null;
    acreage: string | null;
}

/** Narrow the admin fold's delta onto the record's shape. */
function fromDelta(delta: AdminLotStateDeltaDecoded): LotStateForRecord {
    return {
        preBiddingAt: delta.preBiddingAt,
        biddingOpenedAt: delta.biddingOpenedAt,
        pausedAt: delta.pausedAt,
        closeTime: delta.closeTime,
        deletedAt: delta.deletedAt,
        withdrawnAt: delta.withdrawnAt,
        hammeredAt: delta.hammeredAt,
        closeProcessedAt: delta.closeProcessedAt,
        bidsPlacedCount: delta.bidsPlacedCount,
        acceptedBidAmount: delta.acceptedBidAmount,
        hammerPrice: delta.hammerPrice,
        winnerCustomerId: delta.winnerCustomerId,
        pricingType: delta.pricingType,
        acreage: delta.acreage,
    };
}

/**
 * One lot's state for the bid record, or `undefined` until it lands.
 *
 * @param lotId - the lot being rendered; `null` yields `undefined`.
 * @returns the record's slice of admin lot state, from whichever
 *   `<RealtimeLotsProvider>` is above — the console's auction-wide fold, or the
 *   unit's own lot-scoped one.
 */
export function useLotStateForRecord(lotId: string | null): LotStateForRecord | undefined {
    // BL-2253: the nullable fold read is shared with the Lot Edit drawer.
    const delta = useOptionalLot(lotId);
    return useMemo(() => (delta ? fromDelta(delta) : undefined), [delta]);
}
